import {findLastIndex, last} from 'lodash';

import {getWindow, isElement, isTextNode} from '../../libs/shared/helpers/src/browser/domHelpers';
import {Anchor} from '../../libs/shared/ui/src/components/reposition/repositionHelpers';

const SURROUNDING_CAPTURE = 200;

export interface SelectionLocation {
  startIndex: number;
  text: string;
  textBefore: string;
  textAfter: string;
  anchor: Anchor;
}

export function isSelectionInsideContainer(selection: Selection | null, container: HTMLElement | null) {
  if (!selection || !container) {
    return false;
  }

  const {anchorNode, focusNode} = selection;

  if (
    !anchorNode ||
    !focusNode ||
    !container.contains(anchorNode) ||
    !container.contains(focusNode) ||
    selection.type !== 'Range' ||
    selection.toString().trim().length === 0
  ) {
    return false;
  }

  return true;
}

export function getLocationFromCurrentSelection(
  selection: Selection | null,
  container: HTMLElement | null,
): SelectionLocation | undefined {
  if (!selection || !container) {
    return undefined;
  }

  // Trivial selection or not fully included in message body?
  if (!isSelectionInsideContainer(selection, container)) {
    return undefined;
  }

  const range = selection.getRangeAt(0);
  const {startContainer, startOffset} = range;

  const internalOffset = isTextNode(startContainer) ? startOffset : 0;
  const text = range.toString();
  const startIndex = internalOffset + getOffsetFromRoot(startContainer, container);
  const endIndex = startIndex + text.length;

  // Slicing strings can break multi-byte characters like emojis. A proper solution would involve using runes.
  const containerTextContent = getTextContent(container);
  const textBefore = containerTextContent.slice(Math.max(0, startIndex - SURROUNDING_CAPTURE), startIndex);
  const textAfter = containerTextContent.slice(endIndex, endIndex + SURROUNDING_CAPTURE);

  return {
    startIndex,
    text: range.toString(),
    textBefore,
    textAfter,
    anchor: () => last(range.getClientRects()) || range.getBoundingClientRect(),
  };
}

function getTextContent(container: Node): string {
  const nodeIterator = getVisibleTextNodeIterator(container);
  if (!nodeIterator) {
    return '';
  }

  // Sum the length of all nodes before the start node.
  const chunks = [];
  let currentNode = nodeIterator.nextNode() as Text | null;
  while (currentNode) {
    chunks.push(currentNode.textContent);
    currentNode = nodeIterator.nextNode() as Text | null;
  }

  return chunks.join('');
}

export function clearCurrentSelection(container: HTMLElement | null) {
  const selection = getSelection(container);
  if (selection) {
    selection.removeAllRanges();
  }
}

export function isSelectionCollapsed(container: HTMLElement | null) {
  const selection = getSelection(container);
  return Boolean(selection?.isCollapsed);
}

export function placeCaretAfterNode(element: HTMLElement) {
  const {parentElement} = element;
  if (!parentElement) {
    return;
  }

  const selection = getSelection(element);
  if (!selection) {
    return;
  }

  const caretElement = getElementForCaret(element);

  const index = Array.from(parentElement.childNodes).indexOf(caretElement);

  const range = new Range();
  range.setStart(parentElement, index + 1);

  selection.removeAllRanges();
  selection.addRange(range);
}

// If element is next to a text node with a zero-width space, place it after the space.
function getElementForCaret(element: HTMLElement): ChildNode {
  const {nextSibling} = element;
  if (!nextSibling) {
    return element;
  }

  if (isTextNode(nextSibling) && nextSibling.textContent === '\u200B') {
    return nextSibling;
  }

  return element;
}

export function placeCaretAtEnd(contentEditable: HTMLElement) {
  const selection = getSelection(contentEditable);
  if (!selection) {
    return;
  }

  // Find last possible editable place.
  const index = findLastIndex(
    Array.from(contentEditable.childNodes),
    (node) => isTextNode(node) || (isElement(node) && node.tagName !== 'BR'),
  );

  const range = new Range();
  range.setStart(contentEditable, index + 1);

  selection.removeAllRanges();
  selection.addRange(range);
}

function getSelection(container: HTMLElement | null) {
  if (!container) {
    return null;
  }

  const window = getWindow(container);
  return window?.getSelection();
}

function getOffsetFromRoot(startNode: Node, container: Node): number {
  const nodeIterator = getVisibleTextNodeIterator(container);
  if (!nodeIterator) {
    return 0;
  }

  // Sum the length of all nodes before the start node.
  let offset = 0;
  let currentNode = nodeIterator.nextNode() as Text | null;
  while (currentNode && currentNode !== startNode) {
    offset += currentNode.length;
    currentNode = nodeIterator.nextNode() as Text | null;
  }

  return offset;
}

function getVisibleTextNodeIterator(container: Node) {
  const document = container.ownerDocument;
  if (!document) {
    return null;
  }

  // Follow of logic of mark.js: include all text nodes, except directly in invisible tags.
  const nodeIterator = document.createNodeIterator(container, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentNode &&
      ['script', 'style', 'title', 'head', 'html'].includes(node.parentNode.nodeName.toLowerCase())
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });

  return nodeIterator;
}
